import React, { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import StatusBadge from "@/components/StatusBadge";
import { addProviderTileLayer } from "@/lib/mapProvider";

// Fix default marker paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function bowserIcon(status) {
  const color =
    status === "in_transit" || status === "assigned" ? "#2563eb" :
    status === "available" ? "#16a34a" :
    status === "maintenance" ? "#f59e0b" : "#6b7280";
  return L.divIcon({
    className: "bowser-marker",
    html: `<div style="background:${color};border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);width:24px;height:24px;border-radius:12px;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:10px;">B</div>`,
    iconSize: [24, 24], iconAnchor: [12, 12],
  });
}

/**
 * BowserMap — imperative Leaflet wrapper. Handles StrictMode double-effect safely
 * via a ref-based init guard + explicit map.remove() on cleanup.
 */
function BowserMap({ positions }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    // Guard: if Leaflet has already tagged this DOM node, skip.
    if (containerRef.current._leaflet_id) return;

    const center = positions.length ? [positions[0].lat, positions[0].lng] : [18.5204, 73.8567];
    const map = L.map(containerRef.current, { center, zoom: 9, zoomControl: true });
    addProviderTileLayer(L, map);
    mapRef.current = map;

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markersRef.current = {};
      }
    };
    // Intentionally run once on mount only — map should initialize once and not re-center on every
    // positions update. Marker sync happens in the second useEffect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync markers whenever positions change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const seen = new Set();
    positions.forEach((p) => {
      seen.add(p.bowser_id);
      const existing = markersRef.current[p.bowser_id];
      if (existing) {
        existing.setLatLng([p.lat, p.lng]);
      } else {
        const m = L.marker([p.lat, p.lng], { icon: bowserIcon(p.status) });
        const orderHtml = p.current_order
          ? `<div style="margin-top:4px;border-top:1px solid #eee;padding-top:4px;"><b>Order ${p.current_order.code}</b><br/>${p.current_order.customer_name}<br/>${p.current_order.site_name || ""}</div>`
          : "";
        m.bindPopup(`<div style="font-size:12px;"><b>${p.bowser_code}</b><br/>${p.registration}<br/>Status: ${p.status}<br/>Stock: ${Math.round(p.current_stock)}L / ${p.capacity}L${orderHtml}</div>`);
        m.bindTooltip(`${p.bowser_code} · ${p.status}`);
        m.addTo(map);
        markersRef.current[p.bowser_id] = m;
      }
    });
    // Remove stale
    Object.keys(markersRef.current).forEach((id) => {
      if (!seen.has(id)) {
        map.removeLayer(markersRef.current[id]);
        delete markersRef.current[id];
      }
    });
  }, [positions]);

  return <div ref={containerRef} style={{ height: "580px", width: "100%", borderRadius: "0.5rem" }} data-testid="live-map" />;
}

export default function LiveTracking() {
  const { data } = useQuery({
    queryKey: ["tracking-live"],
    queryFn: () => api.get("/tracking/live").then((r) => r.data),
    refetchInterval: 8000,
  });
  const positions = data?.items || [];

  return (
    <div className="space-y-4" data-testid="tracking-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Live Bowser Tracking</h1>
        <p className="text-sm text-muted-foreground">{positions.length} bowsers · updated every 8 seconds (simulated positions)</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <Card className="lg:col-span-3">
          <CardContent className="p-0">
            <BowserMap positions={positions} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Fleet on-road</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {positions.map((p) => (
              <div key={p.bowser_id} className="rounded border border-border/60 p-3">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-sm font-bold">{p.bowser_code}</div>
                  <StatusBadge status={p.status} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{p.registration}</div>
                <div className="mt-1 text-xs">Stock: <b className="font-mono">{Math.round(p.current_stock)}L</b></div>
                {p.current_order && (
                  <Link to="/app/orders" className="mt-1 block text-xs text-primary hover:underline">
                    → {p.current_order.code} · {p.current_order.customer_name}
                  </Link>
                )}
              </div>
            ))}
            {positions.length === 0 && <div className="py-4 text-center text-sm text-muted-foreground">No bowsers configured yet.</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
